import type { TJSONSchema } from "@artifact-management/json-dynamic-form";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton, InputAdornment, MenuItem, TextField } from "@mui/material";
import { Controller, type FieldValues, type Path, type PathValue } from "react-hook-form";
import type { IFormGenInputProps } from "./index";
import { getFormGenEnumLabels, getFormGenEnumOptionKey, getFormGenEnumOptions } from "./logics";

export const FormGenInputSelect = <Schema extends TJSONSchema, T extends FieldValues>({
  control,
  uiSchemaElement,
  multiple,
  jsonSchemaElement,
  enumOptions,
}: IFormGenInputProps<Schema, T>) => {
  if (multiple) {
    throw new Error("Multiple select is not supported yet");
  }
  const options = getFormGenEnumOptions(enumOptions, getFormGenEnumLabels(jsonSchemaElement, multiple));

  return (
    <Controller
      control={control}
      name={uiSchemaElement.scope as unknown as Path<T>}
      defaultValue={undefined as unknown as PathValue<T, Path<T>>}
      render={({ field, fieldState }) => (
        <TextField
          label={uiSchemaElement.label}
          value={getFormGenEnumOptionKey(field.value)}
          onChange={(event) =>
            field.onChange(options.find((option) => option.key === event.target.value)?.value ?? undefined)
          }
          onBlur={field.onBlur}
          error={Boolean(fieldState.error)}
          helperText={fieldState.error?.message}
          inputRef={field.ref}
          size="small"
          fullWidth
          disabled={uiSchemaElement.disabled}
          select
          slotProps={{
            input: {
              endAdornment: field.value && !uiSchemaElement.disabled && (
                <InputAdornment position="end" sx={{ pr: 3 }}>
                  <IconButton size="small" onClick={() => field.onChange(undefined)}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        >
          {options.map((option) => (
            <MenuItem key={option.key} value={option.key}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      )}
    />
  );
};
